import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useFilterContext } from "../../../hooks/FilterContext";
import { useLazyExportAdviceRegisterQuery, useLazyFetchAdviceRegisterQuery } from "../../../services/clientreports";

const limit = 25;

const useAdviceRegister = (defaultSort = "compliantDate") => {
    const { filter, isReady } = useFilterContext();
    const [sort, setSort] = useState(defaultSort);
    const [cacheKey, setCacheKey] = useState(new Date().valueOf());
    const [isClearing, setIsClearing] = useState(false);
    const [fetchTrigger, { data, isFetching, isError, error }] = useLazyFetchAdviceRegisterQuery()

    const { pagination, results } = data || { pagination: { page: 1, pageSize: limit, totalCount: 0, totalPages: 1 }, results: [] };
    const { page, totalCount, totalPages } = pagination;
    const hasMore = totalCount > 0 && page < totalPages;
    const hasData = !isClearing && !isError;

    const clear = () => {
        if (isFetching || !isReady)
            return;

        const newCacheKey = new Date().valueOf();
        setIsClearing(true);
        fetchTrigger({
            cacheKey: newCacheKey,
            page: 1,
            limit,
            sort,
            ...filter
        }).unwrap()
            .then(_ => setCacheKey(newCacheKey))
            .finally(() => setIsClearing(false));
    }

    const isRowLoaded = (index) => !hasMore || index < results.length;

    const loadMore = () => {
        if (isFetching)
            return;

        return fetchTrigger({
            cacheKey,
            page: page + 1,
            limit,
            sort,
            ...filter
        }).unwrap();
    }

    const sortTable = (property) => {
        let newDirection = "";
        let [sortProperty, direction] = sort.split("+");

        if (property === sortProperty)
            newDirection = direction == null ? "+DESC" : null;

        setSort(property + (newDirection ?? ""));
    }

    useEffect(clear, [filter, isReady, sort]);

    const [exportTrigger] = useLazyExportAdviceRegisterQuery();

    const exportAndOpen = async () => {
        const res = await exportTrigger(filter).unwrap();

        const { blob, filename } = res;
        const url = window.URL.createObjectURL(blob);

        // Create a temporary link to trigger the download with set filename
        const tempAnchor = document.createElement('a');
        tempAnchor.href = url;
        tempAnchor.download = filename;
        document.body.appendChild(tempAnchor);
        tempAnchor.click();

        // Clean up the URL object
        document.body.removeChild(tempAnchor);
        window.URL.revokeObjectURL(url);

        return;
    }

    const exportCsv = () => {
        return toast.promise(exportAndOpen().unwrap(), {
            loading: "Exporting Register CSV",
            success: "Export completed!",
            error: (err) => {
                console.error(err);
                return `There was an error exporting register: ${err.message ?? "Unknown Error"}`
            }
        })
    }

    return {
        clear,
        data: hasData ? results : [],
        isFetching,
        isClearing,
        error,
        isRowLoaded,
        loadMore,
        currentSort: sort,
        sortTable,
        totalCount: hasData ? totalCount : 0,
        exportCsv
    }
}

export default useAdviceRegister;